import type { MaintenanceSlot, MaintenanceTimeSpan } from '@/lib/types/maintenance';
import type { Resource } from '@/lib/types/resource';
import { useMaintenancesStore } from '@/store/maintenance';
import { useResourcesStore } from '@/store/resource';
import { parseDateTime } from '@/utils/date';
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';

export interface ResourceWithTimespans extends Resource {
    timespans: MaintenanceTimeSpan[];
}

export interface MaintenanceSlotForm extends Omit<MaintenanceSlot, 'validFrom' | 'validUntil' | 'start' | 'end'> {
    validFromDate: Date | null;
    validUntilDate: Date | null;
    startTime: string | null;
    endTime: string | null;
}

export function useMaintenancesPage() {
    const { t } = useI18n();
    const resourceStore = useResourcesStore();
    const maintenanceStore = useMaintenancesStore();

    // State
    const loading = ref(true);
    const selectedQuickPeriod = ref<string>('thisMonth');
    const dateRange = ref<Date[]>([getFirstDayOfMonth(new Date()), getLastDayOfMonth(new Date())]);

    // Helper functions for date manipulation
    function getFirstDayOfMonth(date: Date): Date {
        return new Date(date.getFullYear(), date.getMonth(), 1);
    }

    function getLastDayOfMonth(date: Date): Date {
        return new Date(date.getFullYear(), date.getMonth() + 1, 0, 23, 59, 59);
    }

    // Computed: Resources with their maintenance timespans
    const allResourcesWithMaintenance = computed<ResourceWithTimespans[]>(() => {
        const now = new Date();

        return resourceStore.resources
            .map(resource => ({
                ...resource,
                timespans: maintenanceStore.timespans
                    .filter(ts => ts.resourceId === resource.id)
                    .sort((a, b) => parseDateTime(a.startUtc).getTime() - parseDateTime(b.startUtc).getTime())
            }))
            .sort((a, b) => {
                const getClosestMaintenance = (resource: ResourceWithTimespans) => {
                    return resource.timespans.find(ts => {
                        const end = parseDateTime(ts.endUtc);
                        return end >= now;
                    });
                };

                const aClosest = getClosestMaintenance(a);
                const bClosest = getClosestMaintenance(b);

                if (!aClosest && !bClosest) return 0;
                if (!aClosest) return 1;
                if (!bClosest) return -1;

                const aTime = parseDateTime(aClosest.startUtc).getTime();
                const bTime = parseDateTime(bClosest.startUtc).getTime();

                return aTime - bTime;
            });
    });

    // Status helpers
    function getStatus(timespan: MaintenanceTimeSpan): 'active' | 'upcoming' | 'completed' {
        const now = new Date();
        const start = parseDateTime(timespan.startUtc);
        const end = parseDateTime(timespan.endUtc);

        if (start <= now && end >= now) return 'active';
        if (start > now) return 'upcoming';
        return 'completed';
    }

    function getStatusLabel(timespan: MaintenanceTimeSpan): string {
        const status = getStatus(timespan);
        return t(`maintenance.status.${status}`);
    }

    function getStatusClass(timespan: MaintenanceTimeSpan) {
        const status = getStatus(timespan);

        const classes = {
            active: {
                bgClass: 'bg-orange-100',
                iconClass: 'text-orange-600',
                badgeClass: 'bg-orange-100 text-orange-700 border border-orange-300'
            },
            upcoming: {
                bgClass: 'bg-blue-100',
                iconClass: 'text-blue-600',
                badgeClass: 'bg-blue-100 text-blue-700 border border-blue-300'
            },
            completed: {
                bgClass: 'bg-gray-100',
                iconClass: 'text-gray-600',
                badgeClass: 'bg-gray-100 text-gray-700 border border-gray-300'
            }
        };

        return classes[status];
    }

    function getResourceStatusClass(resource: ResourceWithTimespans) {
        const hasActive = resource.timespans.some(ts => getStatus(ts) === 'active');
        const hasUpcoming = resource.timespans.some(ts => getStatus(ts) === 'upcoming');

        if (hasActive) {
            return {
                bgClass: 'bg-orange-100',
                iconClass: 'text-orange-600',
                // 'warn', never 'warning' — PrimeVue 4.3.2's style module branches on 'warn' and lets 'warning'
                // fall through to default styling with no error (CalendarEventPreviewCard.vue:32-37).
                severity: 'warn' as const
            };
        }
        if (hasUpcoming) {
            return {
                bgClass: 'bg-blue-100',
                iconClass: 'text-blue-600',
                severity: 'info' as const
            };
        }
        return {
            bgClass: 'bg-green-100',
            iconClass: 'text-green-600',
            severity: 'success' as const
        };
    }

    function getResourceStatusText(resource: ResourceWithTimespans): string {
        const active = resource.timespans.filter(ts => getStatus(ts) === 'active').length;
        const upcoming = resource.timespans.filter(ts => getStatus(ts) === 'upcoming').length;

        if (active > 0) return t('maintenance.inMaintenance');
        if (upcoming > 0) return t('maintenance.maintenanceScheduled');
        return t('maintenance.available');
    }

    function getMaintenanceColor(timespan: MaintenanceTimeSpan): string {
        const status = getStatus(timespan);
        if (status === 'active') return 'bg-orange-500';
        if (status === 'upcoming') return 'bg-blue-500';
        return 'bg-gray-400';
    }

    function getMaintenanceBorderClass(timespan: MaintenanceTimeSpan): string {
        const status = getStatus(timespan);
        if (status === 'active') return 'border-orange-300 bg-orange-50';
        if (status === 'upcoming') return 'border-blue-300 bg-blue-50';
        return 'border-gray-300 bg-gray-50';
    }

    // Timeline helpers
    function getTimelineStart(): string {
        if (!dateRange.value || !dateRange.value[0]) return '';
        return formatDate(dateRange.value[0]);
    }

    function getTimelineEnd(): string {
        if (!dateRange.value || !dateRange.value[1]) return '';
        return formatDate(dateRange.value[1]);
    }

    function getTodayPosition(): string {
        if (!dateRange.value || dateRange.value.length < 2 || !dateRange.value[0] || !dateRange.value[1]) {
            return '-100%';
        }

        const today = new Date().getTime();
        const start = dateRange.value[0].getTime();
        const end = dateRange.value[1].getTime();

        if (today < start || today > end) return '-100%';

        const total = end - start;
        const elapsed = today - start;
        const percentage = (elapsed / total) * 100;

        return `${Math.max(0, Math.min(100, percentage))}%`;
    }

    function getMaintenancePosition(timespan: MaintenanceTimeSpan) {
        if (!dateRange.value || dateRange.value.length < 2 || !dateRange.value[0] || !dateRange.value[1]) {
            return { left: '0%', width: '0%' };
        }

        const start = dateRange.value[0].getTime();
        const end = dateRange.value[1].getTime();
        const totalTime = end - start;

        const maintenanceStart = parseDateTime(timespan.startUtc).getTime();
        const maintenanceEnd = parseDateTime(timespan.endUtc).getTime();

        const visibleStart = Math.max(maintenanceStart, start);
        const visibleEnd = Math.min(maintenanceEnd, end);

        const leftPos = ((visibleStart - start) / totalTime) * 100;
        const width = ((visibleEnd - visibleStart) / totalTime) * 100;

        return {
            left: `${Math.max(0, leftPos)}%`,
            width: `${Math.min(100 - Math.max(0, leftPos), width)}%`
        };
    }

    // Formatting helpers
    function formatDate(date: Date): string {
        const d = new Date(date);
        const pad = (n: number) => String(n).padStart(2, '0');
        return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}`;
    }

    function formatDateRange(timespan: MaintenanceTimeSpan): string {
        const start = parseDateTime(timespan.startUtc);
        const end = parseDateTime(timespan.endUtc);
        return `${formatDate(start)} - ${formatDate(end)}`;
    }

    function formatTimeRange(timespan: MaintenanceTimeSpan): string {
        const start = parseDateTime(timespan.startUtc);
        const end = parseDateTime(timespan.endUtc);
        const pad = (n: number) => String(n).padStart(2, '0');
        return `${pad(start.getHours())}:${pad(start.getMinutes())} - ${pad(end.getHours())}:${pad(end.getMinutes())}`;
    }

    // Quick period selection
    const quickPeriodOptions = computed(() => [
        { label: t('maintenance.period.thisMonth'), value: 'thisMonth' },
        { label: t('maintenance.period.nextMonth'), value: 'nextMonth' },
        { label: t('maintenance.period.next3Months'), value: 'next3Months' },
        { label: t('maintenance.period.thisYear'), value: 'thisYear' }
    ]);

    function applyQuickPeriod() {
        const now = new Date();
        let start: Date;
        let end: Date;

        switch (selectedQuickPeriod.value) {
            case 'thisMonth':
                start = getFirstDayOfMonth(now);
                end = getLastDayOfMonth(now);
                break;
            case 'nextMonth':
                start = getFirstDayOfMonth(new Date(now.getFullYear(), now.getMonth() + 1, 1));
                end = getLastDayOfMonth(new Date(now.getFullYear(), now.getMonth() + 1, 1));
                break;
            case 'next3Months':
                start = getFirstDayOfMonth(now);
                end = getLastDayOfMonth(new Date(now.getFullYear(), now.getMonth() + 3, 0));
                break;
            case 'thisYear':
                start = new Date(now.getFullYear(), 0, 1);
                end = new Date(now.getFullYear(), 11, 31, 23, 59, 59);
                break;
            default:
                start = getFirstDayOfMonth(now);
                end = getLastDayOfMonth(now);
        }

        dateRange.value = [start, end];
        fetchTimespans();
    }

    function onDateRangeChange() {
        if (dateRange.value && dateRange.value.length === 2 && dateRange.value[0] && dateRange.value[1]) {
            fetchTimespans();
        }
    }

    async function fetchTimespans() {
        if (!dateRange.value || dateRange.value.length < 2 || !dateRange.value[0] || !dateRange.value[1]) {
            return;
        }

        loading.value = true;
        try {
            const formatDateForApi = (date: Date): string => {
                const year = date.getFullYear();
                const month = String(date.getMonth() + 1).padStart(2, '0');
                const day = String(date.getDate()).padStart(2, '0');
                return `${year}-${month}-${day}`;
            };

            await maintenanceStore.fetchTimeSpans(
                formatDateForApi(dateRange.value[0]),
                formatDateForApi(dateRange.value[1])
            );
        } finally {
            loading.value = false;
        }
    }

    async function initialize() {
        loading.value = true;
        try {
            await resourceStore.fetchAll();
            await fetchTimespans();
        } finally {
            loading.value = false;
        }
    }

    return {
        // State
        loading,
        dateRange,
        selectedQuickPeriod,
        quickPeriodOptions,

        // Computed
        allResourcesWithMaintenance,

        // Status helpers
        getStatus,
        getStatusLabel,
        getStatusClass,
        getResourceStatusClass,
        getResourceStatusText,
        getMaintenanceColor,
        getMaintenanceBorderClass,

        // Timeline helpers
        getTimelineStart,
        getTimelineEnd,
        getTodayPosition,
        getMaintenancePosition,

        // Formatting helpers
        formatDate,
        formatDateRange,
        formatTimeRange,

        // Actions
        applyQuickPeriod,
        onDateRangeChange,
        fetchTimespans,
        initialize
    };
}
